import React, { useState, useEffect, useRef, memo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { WorkflowCanvas } from '../workflows/WorkflowCanvas';
import { WorkflowCanvasMode } from '../workflows/types';
import * as sessionService from '../../services/sessionService';
import { useParams, useNavigate } from 'react-router-dom';
import ErrorBoundary from '../common/ErrorBoundary';
import { useDebouncedApiCall } from '../../utils/apiUtils';

interface PhaseDependencyGraphProps {
  workflowId?: string;
  height?: string;
  onPhaseClick?: (phase: any) => void;
  executionId?: string;
  executionData?: any; // Add this prop
  disablePolling?: boolean; // Add this prop
}

export const PhaseDependencyGraph: React.FC<PhaseDependencyGraphProps> = (props) => {
  return (
    <ErrorBoundary>
      <PhaseDependencyGraphContent {...props} />
    </ErrorBoundary>
  );
};

// Use memo to prevent unnecessary rerenders
const PhaseDependencyGraphContent: React.FC<PhaseDependencyGraphProps> = memo(({
  workflowId: propWorkflowId,
  height = '600px',
  onPhaseClick,
  executionId,
  executionData: propExecutionData,
  disablePolling = false
}) => {
  const { workflowId: paramWorkflowId } = useParams<{ workflowId: string }>();
  const workflowId = propWorkflowId || paramWorkflowId;
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  
  const [workflow, setWorkflow] = useState<any>(null);
  const [executionData, setExecutionData] = useState<any>(propExecutionData || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [viewMode, setViewMode] = useState<'default' | 'timeline' | 'parallel'>('default');
  
  // Refs to track mounting state and prevent unnecessary updates
  const isMounted = useRef(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousExecutionDataRef = useRef<any>(null);
  
  // Track mounting/unmounting
  useEffect(() => {
    console.log('[MOUNT-DEBUG] PhaseDependencyGraph mounted with workflowId:', workflowId);
    console.log('[MOUNT-DEBUG] executionId:', executionId);
    console.log('[MOUNT-DEBUG] disablePolling:', disablePolling);
    
    // Set mounted flag
    isMounted.current = true;
    
    return () => {
      console.log('[MOUNT-DEBUG] PhaseDependencyGraph unmounted');
      // Clear mounted flag to prevent state updates after unmount
      isMounted.current = false;
    };
  }, [workflowId, executionId, disablePolling]);
  
  // Create debounced API calls
  const [debouncedGetWorkflow] = useDebouncedApiCall(sessionService.getWorkflow, 300);
  const retryCount = useRef(0);
  const MAX_RETRIES = 5;
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch workflow data with retry logic
  useEffect(() => {
    const fetchWorkflow = async () => {
      if (!workflowId || !isMounted.current) return;
      
      try {
        setLoading(true);
        console.log(`PhaseDependencyGraph: Fetching workflow with ID ${workflowId}`);
        
        const data = await debouncedGetWorkflow(workflowId);
        
        // Only update state if component is still mounted
        if (isMounted.current) {
          console.log('PhaseDependencyGraph: Workflow data received:', data);
          setWorkflow(data);
          setError(null);
          retryCount.current = 0; // Reset retry count on success
        }
      } catch (err) {
        if (isMounted.current) {
          const errorMessage = err.response?.status === 429 
            ? "Rate limit exceeded. Retrying shortly..." 
            : "Failed to fetch workflow";
            
          setError(errorMessage);
          console.error('PhaseDependencyGraph: Error fetching workflow:', err);
          
          // Implement exponential backoff for retries
          if (retryCount.current < MAX_RETRIES) {
            const delay = Math.min(1000 * (2 ** retryCount.current), 30000); // Cap at 30 seconds
            console.log(`Retrying in ${delay}ms (attempt ${retryCount.current + 1}/${MAX_RETRIES})`);
            
            retryTimeoutRef.current = setTimeout(() => {
              retryCount.current++;
              fetchWorkflow();
            }, delay);
          }
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };
    
    fetchWorkflow();
    
    // Clean up on unmount or when workflowId changes
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [workflowId, debouncedGetWorkflow]);
  
  // Fetch execution data if executionId is provided
  useEffect(() => {
    if (!executionId || disablePolling) return;
    
    console.log('[EXECUTION-DEBUG] Setting up execution data fetching for executionId:', executionId);
    
    // If polling is disabled, just use the provided execution data
    if (disablePolling) {
      console.log('[EXECUTION-DEBUG] Polling disabled, using provided execution data');
      if (propExecutionData) {
        setExecutionData(propExecutionData);
        previousExecutionDataRef.current = propExecutionData;
      }
      return;
    }
    
    const fetchExecution = async () => {
      if (!isMounted.current) {
        console.log('[EXECUTION-DEBUG] Component not mounted, skipping fetch');
        return;
      }
      
      try {
        const data = await sessionService.getWorkflowExecution(executionId);
        
        // Only update state if data has changed and component is still mounted
        if (isMounted.current) {
          // Use a more reliable way to compare objects
          const currentDataStr = JSON.stringify(data);
          const prevDataStr = JSON.stringify(previousExecutionDataRef.current);
          
          if (prevDataStr !== currentDataStr) {
            console.log('[EXECUTION-DEBUG] Execution data changed, updating state');
            setExecutionData(data);
            previousExecutionDataRef.current = data;
          } else {
            console.log('[EXECUTION-DEBUG] Execution data unchanged, skipping update');
          }
        }
      } catch (err) {
        console.error("Failed to fetch execution data", err);
      }
    };
    
    // Initial fetch
    fetchExecution();
    
    // Clear any existing interval
    if (intervalRef.current) {
      console.log('[EXECUTION-DEBUG] Clearing existing polling interval:', intervalRef.current);
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Set up polling for updates with a ref to track the interval
    console.log('[EXECUTION-DEBUG] Setting up polling interval');
    intervalRef.current = setInterval(() => {
      console.log('[EXECUTION-DEBUG] Polling for execution updates');
      fetchExecution();
    }, 15000); // 15 seconds to reduce load
    
    // Clean up on unmount or when executionId changes
    return () => {
      console.log('[EXECUTION-DEBUG] Cleaning up polling interval');
      if (intervalRef.current) {
        console.log('[EXECUTION-DEBUG] Clearing interval:', intervalRef.current);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        console.log('[EXECUTION-DEBUG] Interval cleared');
      }
    };
  }, [executionId, disablePolling, propExecutionData]); // Only these dependencies
  
  // Handle going back to workflow editor
  const handleBackToEditor = () => {
    navigate(`/workflows/${workflowId}/edit`);
  };
  
  if (loading) return <div className="p-4">Loading workflow dependencies...</div>;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;
  if (!workflow) return <div className="p-4">No workflow data available</div>;
  
  return (
    <div className="phase-dependency-graph" style={{ height }}>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Phase Dependencies: {workflow.name}</h3>
          <p className="text-sm text-gray-600">{workflow.description}</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <div>
            <label className="mr-2 text-sm">View Mode:</label>
            <select
              className={`border rounded px-2 py-1 ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as any)}
            >
              <option value="default">Default</option>
              <option value="timeline">Timeline</option>
              <option value="parallel">Parallel Execution</option>
            </select>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="criticalPath"
              checked={showCriticalPath}
              onChange={(e) => setShowCriticalPath(e.target.checked)}
              className={`mr-1 ${darkMode ? 'bg-gray-700 border-gray-600' : ''}`}
            />
            <label htmlFor="criticalPath" className={`text-sm ${darkMode ? 'text-gray-300' : ''}`}>Show Critical Path</label>
          </div>
          
          <button
            className="px-3 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            onClick={handleBackToEditor}
          >
            Back to Editor
          </button>
        </div>
      </div>
      
      <div className="border rounded-lg overflow-hidden" style={{ height: 'calc(100% - 60px)' }}>
        <WorkflowCanvas
          workflowData={workflow}
          mode={WorkflowCanvasMode.VIEW}
          height="100%"
          readOnly={true}
          executionData={executionData}
        />
      </div>
    </div>
  );
});

// Export with memo to prevent unnecessary rerenders
export default memo(PhaseDependencyGraph, (prevProps, nextProps) => {
  // Only rerender if these props change
  return prevProps.workflowId === nextProps.workflowId && 
         prevProps.executionId === nextProps.executionId &&
         prevProps.height === nextProps.height &&
         prevProps.disablePolling === nextProps.disablePolling;
});
